import React from 'react';

const formatPct = (v) => `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

/** P&L% chart hover: date, account day %, cumulative P&L%, index, and account balance. */
export default function PnlPercentChartTooltip({
  active,
  payload,
  label,
  colors,
  formatDateLabel,
  showBenchmark = false,
  benchmarkSymbol = null,
  benchmarkColor = null,
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0]?.payload || {};
  const pnlEntry = payload.find((p) => p.dataKey === 'pnlPct');
  const pnlValue =
    pnlEntry?.value != null
      ? pnlEntry.value
      : point?.pnlPct != null
        ? point.pnlPct
        : null;

  const dayPnlPct =
    point?.dayPnlPct != null && Number.isFinite(Number(point.dayPnlPct))
      ? Number(point.dayPnlPct)
      : null;

  const benchmarkEntry = payload.find((p) => p.dataKey === 'benchmarkPct');
  const benchmarkValue = benchmarkEntry?.value ?? point?.benchmarkPct;
  const hasBenchmark =
    showBenchmark && benchmarkValue != null && Number.isFinite(Number(benchmarkValue));

  const row = { display: 'flex', justifyContent: 'space-between', gap: 16, marginTop: 4 };
  const labelWithDot = { display: 'inline-flex', alignItems: 'center', gap: 6, color: colors.textMuted };

  return (
    <div
      style={{
        background: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '10px 12px',
        fontSize: '0.8rem',
      }}
      data-testid="pnl-percent-chart-tooltip"
    >
      <div style={{ color: colors.text, fontWeight: 600, marginBottom: 6 }}>
        {formatDateLabel(label)}
      </div>
      {dayPnlPct != null && (
        <div style={row}>
          <span style={{ color: colors.textMuted }}>Account (day)</span>
          <span
            style={{
              fontWeight: 700,
              color: dayPnlPct >= 0 ? colors.chartUp : colors.chartDown,
            }}
            data-testid="tooltip-day-pnl-pct"
          >
            {formatPct(dayPnlPct)}
          </span>
        </div>
      )}
      {pnlValue != null && (
        <div style={row}>
          <span style={hasBenchmark ? labelWithDot : { color: colors.textMuted }}>
            {hasBenchmark && (
              <span
                style={{ width: 8, height: 8, borderRadius: '50%', background: colors.primary, flexShrink: 0 }}
              />
            )}
            P&L%
          </span>
          <span
            style={{
              fontWeight: 600,
              color: Number(pnlValue) >= 0 ? colors.chartUp : colors.chartDown,
            }}
            data-testid="tooltip-pnl-pct"
          >
            {formatPct(pnlValue)}
          </span>
        </div>
      )}
      {hasBenchmark && (
        <div style={row}>
          <span style={labelWithDot}>
            <span
              style={{ width: 8, height: 8, borderRadius: '50%', background: benchmarkColor, flexShrink: 0 }}
            />
            {benchmarkSymbol}
          </span>
          <span style={{ color: colors.text, fontWeight: 600 }}>{formatPct(benchmarkValue)}</span>
        </div>
      )}
      {!!point?.transferAmount && (
        <div style={row}>
          <span style={{ color: colors.textMuted }}>
            {point.transferAmount > 0 ? 'Deposit' : 'Withdrawal'}
          </span>
          <span style={{ color: colors.text, fontWeight: 600 }} data-testid="tooltip-transfer">
            {point.transferAmount > 0 ? '+' : '−'}$
            {Math.abs(Number(point.transferAmount)).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      )}
      {point?.equity != null && (
        <div style={row}>
          <span style={{ color: colors.textMuted }}>Account balance</span>
          <span style={{ color: colors.text, fontWeight: 600 }}>
            $
            {Number(point.equity).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      )}
    </div>
  );
}
